import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, message } from 'antd';
import { ventaService } from '../../services/api';

const { Option } = Select;

// ── Buscador de comprador (usuario) reutilizable ─────────────────────────────
// Encapsula el patrón repetido en Ventas: busca usuarios contra
// ventaService.buscarUsuarios (mínimo 2 caracteres) y los ofrece en un Select
// con showSearch. Antes vivía duplicado en VentaForm, ModalEditarVenta y
// MatchModal.
//
// Props:
//   value          → id_usuario seleccionado (controlado por el padre)
//   onChange       → (id_usuario) => void
//   placeholder    → texto del Select
//   extraUsuarios  → usuarios inyectados por el padre que deben figurar en las
//                    opciones aunque no vengan de la búsqueda: el comprador ya
//                    vendido (pre-carga) o uno recién creado. Se mergean primero
//                    y se deduplican por id_usuario.
//   initialQuery   → si viene, dispara una búsqueda al montar / al cambiar
//                    (caso MatchModal: pre-busca con el comprador_texto libre).
const SelectComprador = ({
  value,
  onChange,
  placeholder = 'Buscar comprador...',
  extraUsuarios = [],
  initialQuery,
  ...rest
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  // reqId: descarta respuestas viejas que resuelven tarde (una lenta no debe
  // pisar los resultados de una búsqueda posterior). debounceRef: agrupa las
  // teclas para no disparar un request por cada una (~300ms).
  const reqId = useRef(0);
  const debounceRef = useRef(null);

  const buscar = useCallback((val) => {
    setBusqueda(val);
    const id = ++reqId.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.length < 2) { setUsuarios([]); setLoadingUsuarios(false); return; }
    setLoadingUsuarios(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await ventaService.buscarUsuarios(val);
        if (id === reqId.current) setUsuarios(res.data);
      } catch (error) {
        if (id === reqId.current) message.error(error.response?.data?.error || 'Error al buscar compradores');
      } finally {
        if (id === reqId.current) setLoadingUsuarios(false);
      }
    }, 300);
  }, []);

  // Limpia el timer pendiente al desmontar.
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Pre-búsqueda opcional al montar / cuando cambia initialQuery.
  useEffect(() => {
    if (initialQuery != null) buscar(initialQuery);
  }, [initialQuery, buscar]);

  // Opciones = extraUsuarios (seed / recién creados) + resultados de búsqueda,
  // deduplicados por id_usuario preservando el orden (extra primero).
  const opciones = useMemo(() => {
    const vistos = new Set();
    return [...extraUsuarios, ...usuarios].filter((u) => {
      if (!u || vistos.has(u.id_usuario)) return false;
      vistos.add(u.id_usuario);
      return true;
    });
  }, [extraUsuarios, usuarios]);

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      filterOption={false}
      onSearch={buscar}
      onChange={onChange}
      loading={loadingUsuarios}
      style={{ width: '100%' }}
      notFoundContent={busqueda.length < 2 ? 'Escribí al menos 2 caracteres' : 'Sin resultados'}
      {...rest}
    >
      {opciones.map((u) => (
        <Option key={u.id_usuario} value={u.id_usuario}>
          {u.nom_usuario} — {u.cuit || u.email_usuario}
        </Option>
      ))}
    </Select>
  );
};

export default SelectComprador;
